/**
 * ServiceTypesPage - Listagem de Tipos de Serviço (Fase 3 OS)
 *
 * Usa os helpers de listagem padrão; ações respeitam a permissão os_service_types.
 *
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial.
 * RELEASE: 2026-10-09 v1.1.0 - Coluna Ordem com bolinha da cor e relógio na Duração.
 */
import { useMemo, useState } from 'react';
import { Clock, MoreHorizontal, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ListPageLayout, ListPageHeader, ListPageFiltersBar, ListPageContent } from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { ServiceTypeFormDialog } from '@/components/service-orders/ServiceTypeFormDialog';
import { STOCK_MOVEMENT_OPTIONS, type ServiceType, useServiceTypes } from '@/hooks/useServiceTypes';

export default function ServiceTypesPage() {
  const { serviceTypes, isLoading, isFetching, refetch, remove } = useServiceTypes();
  const { canCreate, canEdit, canDelete, isAdmin } = usePermissionsContext() as any;
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ServiceType | null>(null);
  const [open, setOpen] = useState(false);

  const allowCreate = isAdmin || canCreate?.('os_service_types');
  const allowEdit = isAdmin || canEdit?.('os_service_types');
  const allowDelete = isAdmin || canDelete?.('os_service_types');

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? serviceTypes.filter((s) => s.name.toLowerCase().includes(q)) : serviceTypes;
  }, [serviceTypes, search]);

  const handleDelete = async (s: ServiceType) => {
    if (!window.confirm(`Excluir o tipo de serviço "${s.name}"?`)) return;
    try { await remove(s.id); toast.success('Tipo de serviço excluído'); }
    catch (e: any) { toast.error(e?.message ?? 'Erro ao excluir'); }
  };

  return (
    <ListPageLayout>
      <ListPageHeader title="Tipos de Serviço"
        actions={<div className="flex gap-2">
          <Button size="icon" variant="outline" aria-label="Atualizar" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </Button>
          {allowCreate && <Button onClick={() => { setEditing(null); setOpen(true); }}><Plus className="mr-1 h-4 w-4" />Novo tipo</Button>}
        </div>} />
      <ListPageFiltersBar isFetching={isFetching}>
        <Input className="max-w-xs" placeholder="Buscar tipo de serviço..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </ListPageFiltersBar>
      <ListPageContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Ordem</TableHead><TableHead>Nome</TableHead><TableHead>Duração</TableHead><TableHead>Estoque</TableHead>
              <TableHead>Veículo</TableHead><TableHead>Checklists</TableHead><TableHead>Situação</TableHead><TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}><TableCell colSpan={8}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
            )) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Nenhum tipo de serviço cadastrado</TableCell></TableRow>
            ) : rows.map((s) => (
              <TableRow key={s.id} className={allowEdit ? 'cursor-pointer' : ''} onClick={() => { if (allowEdit) { setEditing(s); setOpen(true); } }}>
                {/* Ordem com a bolinha da cor do tipo (modelo enviado pelo usuário) */}
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: s.color }} />
                    <span className="tabular-nums">{s.sort_order}</span>
                  </div>
                </TableCell>
                <TableCell><span className="font-medium">{s.name}</span></TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                    <Clock className="h-4 w-4 text-muted-foreground" />{s.duration_minutes} min
                  </span>
                </TableCell>
                <TableCell>{STOCK_MOVEMENT_OPTIONS.find((o) => o.value === s.stock_movement)?.label}</TableCell>
                <TableCell>{s.requires_vehicle ? 'Exige' : '—'}</TableCell>
                <TableCell>{s.checklist_templates.length}</TableCell>
<TableCell>
                  {/* Badge de status no mesmo formato da listagem de Endereços de Estoque */}
                  <Badge variant="outline" className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider">
                    <StatusDot tone={s.is_active ? 'emerald' : 'muted'} />{s.is_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {(allowEdit || allowDelete) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" aria-label="Ações"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {allowEdit && <DropdownMenuItem onClick={() => { setEditing(s); setOpen(true); }}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>}
                        {allowDelete && <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(s)}><Trash2 className="mr-2 h-4 w-4" />Excluir</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListPageContent>
      <ServiceTypeFormDialog open={open} onOpenChange={setOpen} serviceType={editing} />
    </ListPageLayout>
  );
}
